import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useConversations } from '../hooks/useConversations';
import { streamChat, parseDocument, generateImage } from '../api/client';
import Sidebar from '../components/Sidebar';
import MessageBubble from '../components/MessageBubble';
import ChatInput from '../components/ChatInput';
import ChatModeSelector from '../components/ChatModeSelector';
import { isAdvancedPackage } from '../utils/plan';
import './Chat.css';

const SUGGESTIONS = [
  'Draft a professional email to a client',
  'Explain a concept like I’m new to it',
  'Help me plan my week',
  'Summarize a block of text for me',
];

export default function Chat() {
  const { user, logout } = useAuth();
  const {
    conversations,
    activeId,
    setActiveId,
    activeConversation,
    createConversation,
    deleteConversation,
    appendMessage,
    appendToLastMessage,
    patchLastMessage,
    patchLastUserMessage,
  } = useConversations(user.id);

  const [collapsed, setCollapsed] = useState(() => window.innerWidth < 780);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [mode, setMode] = useState('simple');
  const [attachedDoc, setAttachedDoc] = useState(null);
  const [attaching, setAttaching] = useState(false);
  const [attachError, setAttachError] = useState('');
  const abortRef = useRef(null);
  const scrollRef = useRef(null);

  const messages = activeConversation?.messages || [];

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [activeConversation?.messages, sending]);

  async function handleSend(text) {
    if (blocked) return;
    setError('');
    const convId = activeConversation.id;

    if (mode === 'image') {
      appendMessage(convId, { role: 'user', content: text });
      appendMessage(convId, { role: 'assistant', content: '' });
      setSending(true);
      try {
        const { image } = await generateImage(text);
        patchLastMessage(convId, { image: image.url });
      } catch (err) {
        patchLastMessage(convId, { content: err.message || 'Image generation failed.' });
      } finally {
        setSending(false);
      }
      return;
    }

    const apiText =
      mode === 'docs' && attachedDoc
        ? `Document "${attachedDoc.filename}":\n\n${attachedDoc.text}\n\n---\n\nUser question: ${text}`
        : text;
    const history = [...messages, { role: 'user', content: apiText }];

    // `pendingRedaction` blocks this conversation from syncing to the server
    // until the screened version of `text` comes back, so the DB never ends
    // up storing raw, unfiltered content.
    appendMessage(convId, { role: 'user', content: text, pendingRedaction: true });
    appendMessage(convId, { role: 'assistant', content: '' });
    setSending(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      await streamChat(history, {
        signal: controller.signal,
        displayText: text,
        conversationId: convId,
        onDelta: (delta) => appendToLastMessage(convId, delta),
        onRedacted: (redactedText, filteredWordCount) => {
          patchLastUserMessage(convId, { content: redactedText, pendingRedaction: false });
          if (filteredWordCount > 0) patchLastMessage(convId, { filteredWordCount });
        },
      });
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message || 'Something went wrong talking to the AI.');
      }
    } finally {
      setSending(false);
      abortRef.current = null;
      // Safety net: if the request failed/aborted before the 'redacted'
      // event arrived, don't leave this conversation stuck un-syncable.
      patchLastUserMessage(convId, { pendingRedaction: false });
    }
  }

  function handleStop() {
    abortRef.current?.abort();
  }

  function handleModeChange(nextMode) {
    setMode(nextMode);
    setAttachedDoc(null);
    setAttachError('');
  }

  async function handleAttach(file) {
    setAttaching(true);
    setAttachError('');
    try {
      const data = await parseDocument(file);
      setAttachedDoc({ filename: data.filename, text: data.text });
    } catch (err) {
      setAttachError(err.message || 'Failed to read document');
    } finally {
      setAttaching(false);
    }
  }

  function handleRemoveAttachment() {
    setAttachedDoc(null);
    setAttachError('');
  }

  function handleNewChat() {
    createConversation();
    if (window.innerWidth < 780) setCollapsed(true);
  }

  function handleSelect(id) {
    setActiveId(id);
    if (window.innerWidth < 780) setCollapsed(true);
  }

  function handleDelete(id) {
    deleteConversation(id);
  }

  const expired = user.isExpired;
  const suspended = user.statusLabel === 'suspended';
  const adminSuspended = user.statusLabel === 'disabled';
  const statusExpired = user.statusLabel === 'expired';
  const blocked = suspended || adminSuspended || statusExpired;
  const canManageBilling = !(user.userType === 2 && user.role === 2);
  const suggestions = messages.length === 0;
  const isAdvanced = isAdvancedPackage(user.packageName);

  return (
    <div className="chat-screen">
      <Sidebar
        user={user}
        conversations={conversations}
        activeId={activeId}
        onSelect={handleSelect}
        onCreate={handleNewChat}
        onDelete={handleDelete}
        onLogout={logout}
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed((v) => !v)}
      />

      <div className="chat-main">
        <header className="chat-topbar">
          {collapsed && (
            <button className="icon-btn" onClick={() => setCollapsed(false)} title="Open sidebar">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M4 5H20M4 12H20M4 19H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          )}
          <div className="chat-topbar-title">{activeConversation?.title || 'New chat'}</div>
          {user.userType === 2 && user.entity?.name && (
            <div className="chat-topbar-org">{user.entity.name}</div>
          )}
          <div className="chat-topbar-plan">{user.packageName || 'No plan'}</div>
        </header>

        {suspended ? (
          <div className="chat-banner chat-banner--error">
            Your account is suspended because of a due invoice.{' '}
            {canManageBilling ? (
              <>
                Please <Link to="/billing">settle it in Billing</Link> to continue chatting.
              </>
            ) : (
              'Please contact your organization administrator to continue chatting.'
            )}
          </div>
        ) : adminSuspended ? (
          <div className="chat-banner chat-banner--error">
            Your account has been suspended. Please contact your company administrator to continue chatting.
          </div>
        ) : statusExpired ? (
          <div className="chat-banner chat-banner--error">
            Your subscription has expired.{' '}
            {canManageBilling ? (
              <>
                Please <Link to="/billing">renew in Billing</Link> to continue chatting.
              </>
            ) : (
              'Please contact your organization administrator to continue chatting.'
            )}
          </div>
        ) : (
          expired && (
            <div className="chat-banner chat-banner--warn">
              Your subscription expired on {user.nextExpiry}. Some features may be limited — contact your
              plan administrator to renew.
            </div>
          )
        )}

        <div className="chat-scroll" ref={scrollRef}>
          {suggestions ? (
            <div className="chat-empty">
              <div className="chat-empty-mark">P</div>
              <h1>How can I help you, {user.fullname?.split(' ')[0]}?</h1>
              <div className="suggestion-grid">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    className="suggestion-card"
                    onClick={() => handleSend(s)}
                    disabled={blocked}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="chat-messages">
              {messages.map((m, i) => (
                <MessageBubble
                  key={i}
                  role={m.role}
                  content={m.content}
                  image={m.image}
                  isStreaming={sending && i === messages.length - 1 && m.role === 'assistant'}
                  filteredWordCount={m.filteredWordCount}
                />
              ))}
              {error && <div className="chat-banner chat-banner--error">{error}</div>}
            </div>
          )}
        </div>

        <ChatModeSelector
          mode={mode}
          onChange={handleModeChange}
          isAdvanced={isAdvanced}
          canUpgrade={canManageBilling}
        />
        <ChatInput
          mode={mode}
          onSend={handleSend}
          onStop={handleStop}
          disabled={sending || blocked}
          isStreaming={sending}
          attachedDoc={attachedDoc}
          attaching={attaching}
          attachError={attachError}
          onAttach={handleAttach}
          onRemoveAttachment={handleRemoveAttachment}
        />
      </div>
    </div>
  );
}
